import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, setDoc, getDoc, increment } from 'firebase/firestore';

// ============================================================
// Nexora / MRAM SMS API configuration
// ============================================================
// IMPORTANT:
// This direct browser integration exposes the API key in the
// built JavaScript bundle. For production, move SMS sending to
// a Firebase Cloud Function/backend and keep the API key there.
//
// MRAM API:
// https://sms.mram.com.bd/smsapi
//
// Required parameters:
// api_key, type, contacts, senderid, msg, label
//
// OTP is a machine-generated notification, so the OTP message
// can remain machine-generated. Use "transactional" label.
// ============================================================


const OTP_EXPIRY_MS = 5 * 60 * 1000;

const TEMP_USER_KEY = 'nexora_pending_registration';
const OTP_STORAGE_KEY = 'nexora_pending_otp';

const normalizePhone = (phone) => {
  let digits = String(phone || '').replace(/\D/g, '');

  if (digits.startsWith('880')) {
    digits = digits.substring(3);
  }

  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }

  return digits.length === 10 ? `+880${digits}` : '';
};


const Register = () => {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [requireOtp, setRequireOtp] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'appSettings', 'registration'))
      .then(snapshot => {
        if (snapshot.exists()) setRequireOtp(snapshot.data()?.requireOtp !== false);
      })
      .catch(() => setRequireOtp(true));
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!fullName || !phoneNumber || !password) {
      setError('দয়া করে সবকটি ঘর পূরণ করুন');
      return;
    }

    if (!acceptedTerms) {
      setError('Please accept the Terms & Conditions before creating an account.');
      return;
    }

    const formattedPhone = normalizePhone(phoneNumber);

    if (!formattedPhone) {
      setError('১১ ডিজিটের সঠিক মোবাইল নম্বর লিখুন (যেমন: 01878602365)');
      return;
    }

    if (password.length < 6) {
      setError('পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে');
      return;
    }

    setIsLoading(true);

    try {
      // ১. আগে চেক করা হচ্ছে এই নম্বরে অ্যাকাউন্ট আছে কি না।
      // OTP সফল হওয়ার আগে নতুন user document তৈরি করা হবে না।
      const userDocRef = doc(db, 'users', formattedPhone);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        setIsLoading(false);
        setError('এই মোবাইল নম্বরে ইতিমধ্যে অ্যাকাউন্ট আছে! লগইন করুন।');
        return;
      }

      if (!requireOtp) {
        await setDoc(userDocRef, {
          uid: formattedPhone,
          fullName: fullName.trim(),
          phoneNumber: formattedPhone,
          password,
          createdAt: new Date().toISOString(),
          isOnline: false,
        });

        await setDoc(
          doc(db, 'totalUser', 'stats'),
          { count: increment(1) },
          { merge: true }
        );

        localStorage.removeItem('nexora_user_uid');
        localStorage.removeItem('nexora_auth_token');
        localStorage.removeItem('nexora_user_token');
        setIsLoading(false);
        navigate('/login');
        return;
      }

      // OTP is generated securely by the Firebase Cloud Function.
      // ৩. OTP is generated and sent by the Firebase Cloud Function.
      const functionsClient = getFunctions();
      const sendNexoraOtp = httpsCallable(
        functionsClient,
        'sendNexoraOtp'
      );

      const result = await sendNexoraOtp({
        phoneNumber: formattedPhone,
        host: window.location.host
      });

      const otpResult = result.data || {};

      if (!otpResult.success || !otpResult.otp) {
        throw new Error('OTP function did not return a valid result');
      }

      const otp = String(otpResult.otp);

      // ৫. OTP সফলভাবে পাঠানো হলে pending registration localStorage-এ রাখা।
      // OTP verify হওয়ার আগে users collection-এ account তৈরি হবে না।
      localStorage.setItem(
        TEMP_USER_KEY,
        JSON.stringify({
          fullName,
          phoneNumber: formattedPhone,
          password,
          createdAt: new Date().toISOString()
        })
      );

      localStorage.setItem(
        OTP_STORAGE_KEY,
        JSON.stringify({
          code: otp,
          phoneNumber: formattedPhone,
          expiresAt: Date.now() + OTP_EXPIRY_MS
        })
      );

      // পুরনো login session পরিষ্কার
      localStorage.removeItem('nexora_user_uid');
      localStorage.removeItem('nexora_auth_token');
      localStorage.removeItem('nexora_user_token');

      setIsLoading(false);

      // OTP verification page
      navigate('/otp-verification');

    } catch (err) {
      console.error('Registration / MRAM SMS Error:', err);

      setIsLoading(false);

      if (String(err?.message || '').includes('Failed to fetch')) {
        setError(
          'OTP সার্ভিসে সংযোগ করা যায়নি। Firebase Function deployment ও configuration চেক করুন।'
        );
      } else {
        setError(
          'OTP পাঠানো যায়নি। Firebase Function এবং MRAM configuration চেক করুন।'
        );
      }
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        * {
          box-sizing: border-box;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .card {
          width: 100%;
          max-width: 400px;
          background: #ffffff;
          border-radius: 28px;
          padding: 32px 24px;
          box-shadow: 0 20px 50px rgba(0, 31, 63, 0.08);
          border: 1px solid #f1f5f9;
          z-index: 10;
        }

        .input-field {
          width: 100%;
          padding: 14px 16px;
          background-color: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 16px;
          font-size: 15px;
          color: #0f172a;
          outline: none;
          transition: all 0.2s ease;
        }

        .input-field:focus {
          border-color: #22c55e;
          background-color: #ffffff;
          box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.15);
        }

        .btn-action {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%);
          border: none;
          border-radius: 16px;
          color: #ffffff;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 10px 25px rgba(22, 163, 74, 0.3);
        }

        .btn-action:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2.5px solid rgba(255,255,255,0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="card">
        <div style={styles.headerGroup}>
          <img src="/logo.png" alt="Nexora Logo" style={styles.logoImage} />

          <h1 style={styles.title}>Create Account</h1>

          <p style={styles.subtitle}>
            Nexora-তে একাউন্ট তৈরি করতে তথ্য দিন
          </p>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        <form onSubmit={handleRegister}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>FULL NAME</label>

            <input
              type="text"
              placeholder="আপনার নাম"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="input-field"
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>PHONE NUMBER</label>

            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span style={styles.prefix}>+880</span>

              <input
                type="tel"
                placeholder="01878602365"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="input-field"
                style={{ paddingLeft: '70px' }}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>PASSWORD</label>

            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="কমপক্ষে ৬ অক্ষরের পাসওয়ার্ড"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                style={{ paddingRight: '60px' }}
              />

              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.toggleBtn}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <label style={styles.termsRow}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={styles.termsCheckbox}
            />
            <span style={styles.termsText}>
              I agree to the{' '}
              <Link to="/terms-conditions" style={styles.termsLink}>
                Terms &amp; Conditions
              </Link>
              {' '}and confirm that I have read them.
            </span>
          </label>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-action"
          >
            {isLoading ? <div className="spinner" /> : requireOtp ? 'Send OTP' : 'Create Account'}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Already have an account?{' '}
            <Link
              to="/login"
              style={{
                color: '#16a34a',
                fontWeight: '700',
                textDecoration: 'none'
              }}
            >
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100dvh',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '32px 20px',
    overflowY: 'auto'
  },
  headerGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '24px',
    textAlign: 'center'
  },
  logoImage: {
    width: '100px',
    height: '100px',
    objectFit: 'contain',
    display: 'block',
    marginBottom: '10px'
  },
  title: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#0f172a',
    margin: '0 0 4px'
  },
  subtitle: {
    fontSize: '12px',
    color: '#64748b',
    margin: 0
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    padding: '10px',
    borderRadius: '12px',
    fontSize: '13px',
    textAlign: 'center',
    marginBottom: '16px'
  },
  inputGroup: {
    marginBottom: '16px'
  },
  label: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#475569',
    display: 'block',
    marginBottom: '6px'
  },
  prefix: {
    position: 'absolute',
    left: '14px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b',
    borderRight: '1px solid #cbd5e1',
    paddingRight: '10px'
  },
  toggleBtn: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    cursor: 'pointer'
  },
  termsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    margin: '2px 0 18px',
    cursor: 'pointer'
  },
  termsCheckbox: {
    width: '17px',
    height: '17px',
    margin: '1px 0 0',
    accentColor: '#16a34a',
    flexShrink: 0,
    cursor: 'pointer'
  },
  termsText: {
    color: '#64748b',
    fontSize: '12px',
    lineHeight: 1.55
  },
  termsLink: {
    color: '#16a34a',
    fontWeight: '700',
    textDecoration: 'underline',
    textUnderlineOffset: '2px'
  },
  footer: {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
    textAlign: 'center'
  },
  footerText: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0
  }
};

export default Register;
